import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { ChildTableImportService } from '../../services/child-table-import.service';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';

@Component({
  selector: 'child-table-toolbar',
  templateUrl: './child-table-toolbar.component.html',
  styleUrls: ['./child-table-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildTableToolbarComponent implements OnDestroy {
  @Input() searchTerm = '';
  @Input() searchPlaceholder = 'Find a record';
  @Input() pageSize = 10;
  @Input() pageSizeOptions = [5, 10, 25, 50, 100];
  @Input() selectedCount = 0;
  @Input() canMoveUp = false;
  @Input() canMoveDown = false;
  @Input() hasCellRange = false;
  @Input() editable = true;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() totalRows = 0;
  @Input() tableTitle = '';
  @Input() columns: { key: string; label: string; type: string }[] = [];
  @Input() hasUnsavedChanges = false;
  @Input() lastSaved: Date | null = null;
  @Input() saving = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() pageSizeChange = new EventEmitter<number | string>();
  @Output() moveUp = new EventEmitter<void>();
  @Output() moveDown = new EventEmitter<void>();
  @Output() moveToPosition = new EventEmitter<number>();
  @Output() copy = new EventEmitter<void>();
  @Output() manualSave = new EventEmitter<void>();
  @Output() deleteSelected = new EventEmitter<void>();
  @Output() clearSelection = new EventEmitter<void>();
  @Output() openImport = new EventEmitter<void>();
  @Output() templateDownload = new EventEmitter<void>();

  get lastSavedText(): string {
    if (!this.lastSaved) return '';
    return this.lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  importMenuOpen = false;
  moveToTarget: number | null = null;
  readonly instanceId = Math.random().toString(36).substring(2, 8);

  private readonly outsideClickListener = (event: MouseEvent) => {
    const dropdown = this.el.nativeElement.querySelector('.import-dropdown');
    if (dropdown && !dropdown.contains(event.target as Node)) {
      this.importMenuOpen = false;
      this.cdr.markForCheck();
      this.removeOutsideClickListener();
    }
  };

  constructor(
    private readonly importService: ChildTableImportService,
    private readonly a11yService: ChildTableA11yService,
    private readonly cdr: ChangeDetectorRef,
    private readonly el: ElementRef<HTMLElement>
  ) {}

  ngOnDestroy(): void {
    this.removeOutsideClickListener();
  }

  toggleImportMenu(): void {
    this.importMenuOpen = !this.importMenuOpen;
    this.cdr.markForCheck();

    if (this.importMenuOpen) {
      this.addOutsideClickListener();
      setTimeout(() => {
        const firstItem = this.el.nativeElement.querySelector('.import-menu .import-option') as HTMLElement;
        firstItem?.focus();
      });
    } else {
      this.removeOutsideClickListener();
    }
  }

  onDropdownFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node)) {
      this.importMenuOpen = false;
      this.removeOutsideClickListener();
      this.cdr.markForCheck();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && !event.shiftKey && !this.importMenuOpen) {
      this.importMenuOpen = true;
      this.addOutsideClickListener();
      this.cdr.detectChanges();
      return;
    }
    if (event.key === 'Escape' && this.importMenuOpen) {
      event.preventDefault();
      this.closeImportMenu(true);
      return;
    }
    if (!this.importMenuOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.importMenuOpen = true;
      this.cdr.markForCheck();
      this.addOutsideClickListener();
      setTimeout(() => {
        const items = this.getMenuItems();
        if (!items.length) return;
        (event.key === 'ArrowUp' ? items[items.length - 1] : items[0])?.focus();
      });
    }
  }

  private addOutsideClickListener(): void {
    document.addEventListener('mousedown', this.outsideClickListener, true);
  }

  private removeOutsideClickListener(): void {
    document.removeEventListener('mousedown', this.outsideClickListener, true);
  }

  private getMenuItems(): HTMLElement[] {
    const menu = this.el.nativeElement.querySelector('.import-menu') as HTMLElement;
    return menu ? Array.from(menu.querySelectorAll('[role="menuitem"]')) : [];
  }

  triggerImport(): void {
    this.openImport.emit();
  }

  downloadTemplate(): void {
    const cols: any[] = this.columns
      .filter((c: any) => c.type !== 'attachment' && c.type !== 'checkbox' && c.type !== 'formula')
      .map((c: any) => ({
        key: c.key,
        label: c.label,
        type: c.type,
        selectOptions: c.selectOptions,
        required: !!(c.validation?.some((v: any) => v.type === 'required')),
      }));
    
    const filename = `${this.tableTitle || 'data'}_template.xlsx`.toLowerCase().replace(/\s+/g, '_');
    this.importService.downloadExcelTemplate(cols, filename);
    this.a11yService.announce('Excel template downloaded');
  }

  onImportMenuKeydown(event: KeyboardEvent): void {
    const items = this.getMenuItems();
    if (!items.length) return;
    const active = document.activeElement as HTMLElement;
    const idx = items.indexOf(active);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[idx >= 0 ? (idx + 1) % items.length : 0]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[idx >= 0 ? (idx - 1 + items.length) % items.length : items.length - 1]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
      case 'Tab':
        event.preventDefault();
        this.closeImportMenu(true);
        break;
    }
  }

  closeImportMenu(returnFocusToTrigger = true): void {
    this.importMenuOpen = false;
    this.removeOutsideClickListener();
    this.cdr.markForCheck();
    if (returnFocusToTrigger) {
      const trigger = this.el.nativeElement.querySelector('.btn-import') as HTMLElement;
      if (trigger) setTimeout(() => trigger.focus(), 0);
    }
  }

  submitMoveToPosition(): void {
    if (this.moveToTarget) {
      this.moveToPosition.emit(this.moveToTarget);
      this.moveToTarget = null;
    }
  }
}
